import { dateLib, propertyLib } from "@agentcall/core/lib"
import { formatPhone, getContractTypeLabel } from "@agentcall/core/lib/tenant_lib"
import { tenantApplicationType } from "@agentcall/core/type"
import { Check, FileText } from "react-feather"
import type {
  TenantApplicationGet200TenantApplication,
  TenantApplicationGet200TenantApplicationFilesItem,
  TenantApplicationGet200TenantApplicationRoom,
} from "@/api/generated/models"

interface TenantApplicationDetailViewProps {
  tenantApplication: TenantApplicationGet200TenantApplication
  room?: TenantApplicationGet200TenantApplicationRoom | null
  files?: TenantApplicationGet200TenantApplicationFilesItem[]
}

interface DetailItemProps {
  label: string
  value: string | React.ReactNode
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-center justify-between">
        <p className="text-[14px] font-semibold leading-[24px] text-[#9eafc7]">{label}</p>
      </div>
      <div className="flex gap-[6px] items-center py-[5px]">
        <div className="shrink-0 size-[18px]">
          <Check className="size-[18px] text-[#9eafc7]" />
        </div>
        <div className="text-[14px] font-medium leading-[20px] text-[#3f4652] tracking-[-0.168px]">
          {value}
        </div>
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  number: number
  children: React.ReactNode
}

function Section({ title, number, children }: SectionProps) {
  return (
    <div className="bg-white border border-[#cbd5e1] border-solid flex flex-col px-[15px] py-[20px] rounded-[6px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
      <div className="flex flex-col gap-[16px]">
        <div className="bg-[#ebf0f6] flex items-center px-[14px] py-[12px]">
          <ol className="text-[14px] font-bold leading-[18px] text-[#354267] tracking-[-0.168px] list-none">
            <li
              className="before:content-[counter(list-item)'.'] before:mr-[7px]"
              style={{ counterIncrement: `list-item ${number}` }}
            >
              {title}
            </li>
          </ol>
        </div>
        <div className="flex flex-col gap-[16px]">{children}</div>
      </div>
    </div>
  )
}

const formatOptionalPhone = (
  phone1: string | null | undefined,
  phone2: string | null | undefined,
  phone3: string | null | undefined
) => {
  const phoneParts = [phone1, phone2, phone3].map((phonePart) => phonePart?.trim() ?? "")
  if (phoneParts.every((phonePart) => phonePart.length === 0)) return "-"
  return formatPhone(phoneParts[0], phoneParts[1], phoneParts[2])
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i

const isImageFile = (file: TenantApplicationGet200TenantApplicationFilesItem) =>
  Boolean(
    file.fileType?.startsWith("image/") ||
      file.fileName?.match(IMAGE_FILE_EXTENSION_REGEX) ||
      file.url?.match(IMAGE_FILE_EXTENSION_REGEX)
  )

export function TenantApplicationDetailView({
  tenantApplication,
  room,
  files,
}: TenantApplicationDetailViewProps) {
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-"
    return dateLib.formatDateLocale(dateString)
  }

  const getPurposeLabel = (purpose: string | null | undefined): string => {
    if (!purpose) return "-"
    const found = tenantApplicationType.TENANT_APPLICATION_PURPOSES.find((p) => p.key === purpose)
    return found?.value || purpose
  }

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "-"
    return `${value.toLocaleString()}円`
  }

  const property = room?.property
  const address = property
    ? [property.prefecture, property.city, property.town, property.district]
        .filter(Boolean)
        .join("")
    : ""

  return (
    <div className="flex flex-col gap-[30px]">
      {/* 申請日 */}
      <div className="flex flex-col gap-[10px]">
        <p className="text-[14px] font-medium leading-[20px] text-[#3f4652] tracking-[-0.168px]">
          申請日：{formatDate(tenantApplication.submittedAt)}
        </p>

        <div className="flex flex-col gap-[20px]">
          {/* ご契約者情報 */}
          <Section title="ご契約者情報" number={1}>
            <DetailItem
              label="契約区分"
              value={getContractTypeLabel(tenantApplication.contractType)}
            />
            <DetailItem label="氏名" value={tenantApplication.name || "-"} />
            <DetailItem label="氏名（カナ）" value={tenantApplication.nameKana || "-"} />
            <DetailItem label="生年月日" value={formatDate(tenantApplication.birthday)} />
            <DetailItem
              label="電話番号"
              value={
                formatPhone(
                  tenantApplication.phone1 || "",
                  tenantApplication.phone2 || "",
                  tenantApplication.phone3 || ""
                ) || "-"
              }
            />
            <DetailItem
              label="電話番号2"
              value={formatOptionalPhone(
                tenantApplication.phone4,
                tenantApplication.phone5,
                tenantApplication.phone6
              )}
            />
            <DetailItem label="メールアドレス" value={tenantApplication.email || "-"} />
            <DetailItem
              label="初回賃貸借契約開始日"
              value={formatDate(tenantApplication.startDate)}
            />
          </Section>

          {/* 物件情報 */}
          <Section title="物件情報" number={2}>
            <DetailItem label="物件名" value={property?.name || "-"} />
            <DetailItem label="物件名（カナ）" value={property?.nameKana || "-"} />
            <DetailItem label="部屋番号" value={room?.name || "-"} />
            <DetailItem
              label="郵便番号"
              value={property ? `〒${property.upperZip}-${property.lowerZip}` : "-"}
            />
            <DetailItem label="住所" value={address || "-"} />
            <DetailItem label="用法" value={propertyLib.typeLabel(property?.type)} />
            <DetailItem label="建物構造" value={propertyLib.structureLabel(property?.structure)} />
            <DetailItem label="用途" value={getPurposeLabel(tenantApplication.purpose)} />
          </Section>

          {/* 部屋情報 */}
          <Section title="部屋情報" number={3}>
            <DetailItem label="家賃" value={formatCurrency(tenantApplication.rent)} />
            <DetailItem
              label="共益費・管理費"
              value={formatCurrency(tenantApplication.commonAreaFee)}
            />
            {tenantApplication.isParking && (
              <div className="flex flex-col gap-0">
                <DetailItem
                  label="駐車場代"
                  value={`区画番号：${tenantApplication.parkingNumber || "-"}`}
                />
                <div className="flex gap-[6px] items-center py-[5px]">
                  <div className="shrink-0 size-[18px]">
                    <Check className="size-[18px] text-[#9eafc7]" />
                  </div>
                  <div className="text-[14px] font-medium leading-[20px] text-[#3f4652] tracking-[-0.168px]">
                    {formatCurrency(tenantApplication.parkingFee)}
                  </div>
                </div>
              </div>
            )}
            {tenantApplication.otherFees &&
              tenantApplication.otherFees.length > 0 &&
              tenantApplication.otherFees.map((fee) => (
                <DetailItem
                  key={fee.label}
                  label={fee.label}
                  value={formatCurrency(fee.price + fee.tax)}
                />
              ))}
          </Section>

          {/* 仲介会社情報 */}
          <Section title="仲介会社情報" number={4}>
            <DetailItem label="仲介会社名" value={tenantApplication.agencyName || "-"} />
            <DetailItem label="仲介会社 電話番号" value={tenantApplication.agencyPhone || "-"} />
            <DetailItem label="その他連絡先" value={tenantApplication.otherContact || "-"} />
          </Section>

          {/* 入居者情報 */}
          <Section title="入居者情報" number={5}>
            {tenantApplication.occupants && tenantApplication.occupants.length > 0 ? (
              tenantApplication.occupants.map((occupant, index) => {
                const occupantWithContact = occupant as typeof occupant & {
                  phone?: string | null
                  note?: string | null
                }
                return (
                  <div
                    key={`${occupant.name}-${occupant.nameKana}-${occupant.birthday}`}
                    className="flex flex-col gap-0"
                  >
                    <DetailItem label={`入居者${index + 1} 氏名`} value={occupant.name || "-"} />
                    <DetailItem label="氏名（カナ）" value={occupant.nameKana || "-"} />
                    <DetailItem label="電話番号" value={occupantWithContact.phone || "-"} />
                    <DetailItem label="生年月日" value={formatDate(occupant.birthday)} />
                    <DetailItem label="備考" value={occupantWithContact.note || "-"} />
                  </div>
                )
              })
            ) : (
              <DetailItem label="入居者" value="-" />
            )}
          </Section>

          {/* 添付画像 */}
          <Section title="画像添付" number={6}>
            {files && files.length > 0 ? (
              files.map((file: TenantApplicationGet200TenantApplicationFilesItem) => (
                <div key={file.id} className="flex flex-col gap-[5px]">
                  <div className="flex items-center justify-between">
                    <p className="text-[14px] font-semibold leading-[24px] text-[#9eafc7]">
                      {file.title || file.fileName || "ファイル"}
                    </p>
                  </div>
                  {isImageFile(file) && file.url && (
                    <div className="h-[165px] rounded-[6px] overflow-hidden bg-slate-50">
                      <img
                        src={file.url}
                        alt={file.title || file.fileName || ""}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  {!isImageFile(file) &&
                    (file.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-[92px] items-center gap-[12px] rounded-[6px] bg-slate-100 px-[16px] text-[#3f4652] hover:bg-slate-200"
                      >
                        <FileText className="size-[32px] shrink-0 text-[#9eafc7]" />
                        <span className="min-w-0 truncate text-[14px] font-semibold leading-[20px]">
                          {file.fileName || file.title || "添付ファイル"}
                        </span>
                      </a>
                    ) : (
                      <div className="flex h-[92px] items-center gap-[12px] rounded-[6px] bg-slate-100 px-[16px] text-[#3f4652]">
                        <FileText className="size-[32px] shrink-0 text-[#9eafc7]" />
                        <span className="min-w-0 truncate text-[14px] font-semibold leading-[20px]">
                          {file.fileName || file.title || "添付ファイル"}
                        </span>
                      </div>
                    ))}
                </div>
              ))
            ) : (
              <div className="flex gap-[6px] items-center py-[5px]">
                <div className="shrink-0 size-[18px]">
                  <Check className="size-[18px] text-[#9eafc7]" />
                </div>
                <div className="text-[14px] font-medium leading-[20px] text-[#9eafc7] tracking-[-0.168px]">
                  添付ファイルがありません
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  )
}
